import { RefObject, useRef, useState } from 'react';

interface Position {
  x: number;
  y: number;
}

interface Dimensions {
  width: number;
  height: number;
}

const useMoveZoom = (parentRef: RefObject<HTMLElement>, childRef: RefObject<HTMLElement>) => {
  const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
  const [scale, setScale] = useState<number>(1);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragStart, setDragStart] = useState<Position>({ x: 0, y: 0 });
  const [parentDimensions, setParentDimensions] = useState<Dimensions>({ width: 0, height: 0 });
  const touchDistanceRef = useRef<number | null>(null);

  const updateParentDimensions = () => {
    // if (parentRef.current) {
    //   const { width, height } = parentRef.current.getBoundingClientRect();
    //   setParentDimensions({ width, height });
    // }
  };

  const handleMouseDownForDrag = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleTouchStartForDrag = (e: React.TouchEvent) => {
    updateParentDimensions();
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      setIsDragging(true);
      setDragStart({ x: touch.clientX - position.x, y: touch.clientY - position.y });
    } else if (e.touches.length === 2) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.sqrt(
        (touch1.clientX - touch2.clientX) ** 2 + (touch1.clientY - touch2.clientY) ** 2
      );
      touchDistanceRef.current = distance;
    }
  };

  const handleMouseMoveForDrag = (e: React.MouseEvent) => {
    if (!isDragging) return;
    const childWidth = childRef.current ? childRef.current.offsetWidth * scale : 0;
    const childHeight = childRef.current ? childRef.current.offsetHeight * scale : 0;

    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;

    setPosition({
      x: Math.max(Math.min(newX, 0), -childWidth + parentDimensions.width),
      y: Math.max(Math.min(newY, 0), -childHeight + parentDimensions.height),
    });
  };

  const handleTouchMoveForDrag = (e: React.TouchEvent) => {
    if (e.touches.length === 1 && isDragging) {
      const touch = e.touches[0];
      const childWidth = childRef.current ? childRef.current.offsetWidth * scale : 0;
      const childHeight = childRef.current ? childRef.current.offsetHeight * scale : 0;

      const newX = touch.clientX - dragStart.x;
      const newY = touch.clientY - dragStart.y;

      setPosition({
        x: Math.max(Math.min(newX, 0), -childWidth + parentDimensions.width),
        y: Math.max(Math.min(newY, 0), -childHeight + parentDimensions.height),
      });
    } else if (e.touches.length === 2) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.sqrt(
        (touch1.clientX - touch2.clientX) ** 2 + (touch1.clientY - touch2.clientY) ** 2
      );

      if (touchDistanceRef.current) {
        const newScale = scale * (distance / touchDistanceRef.current);
        setScale(Math.max(newScale, 0.1)); // Prevent scaling below 0.1
      }

      touchDistanceRef.current = distance;
    }
  };

  const handleMouseUpForDragEnd = () => {
    setIsDragging(false);
  };

  const handleTouchEndForDragEnd = () => {
    setIsDragging(false);
    touchDistanceRef.current = null;
  };

  const handleWheelForZoom = (e: React.WheelEvent) => {
    e.preventDefault();
    const zoomStep = 0.1;
    const newScale = e.deltaY < 0 ? scale + zoomStep : scale - zoomStep;

    setScale(Math.max(newScale, 0.1)); // Prevent scaling below 0.1
  };

  const childStyle: React.CSSProperties = {
    transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
    transformOrigin: '0 0',
  };

  return {
    childStyle,
    isDragging,
    handleMouseDownForDrag,
    handleTouchStartForDrag,
    handleMouseMoveForDrag,
    handleTouchMoveForDrag,
    handleMouseUpForDragEnd,
    handleTouchEndForDragEnd,
    handleWheelForZoom,
  };
};

export default useMoveZoom;
